import { useState } from "react";
import { ReservationFormData } from "../types";
import { INITIAL_FORM_DATA } from "../constants";
import { VEHICLES } from "@/config/infos";
import type { Locale } from "@/config/i18n";
import { useAddressAutocomplete } from "./useAddressAutocomplete";
import { usePriceEstimate } from "./usePriceEstimate";
import { useReservationSubmit } from "./useReservationSubmit";
import { useStops } from "./useStops";

/**
 * Hook racine du formulaire de réservation : détient l'état `formData`, dérive
 * les infos vol/train + le véhicule sélectionné, et COMPOSE des hooks focalisés
 * (autocomplétion d'adresses, estimation de prix, soumission, étapes).
 *
 * La forme de l'objet retourné est INCHANGÉE par rapport à l'implémentation
 * monolithique précédente : ce découpage est un pur déplacement de code, sans
 * aucun changement de comportement.
 */
export const useReservationForm = (
  isMinimal: boolean,
  locale: Locale = "fr",
) => {
  const [formData, setFormData] = useState<ReservationFormData>({
    ...INITIAL_FORM_DATA,
    departureFlightNumber: "",
    arrivalFlightNumber: "",
    departureTrainNumber: "",
    arrivalTrainNumber: "",
  });

  // Nouveaux états pour les informations de vol et de train
  const departureFlightInfo = {
    number: formData.departureFlightNumber,
    isDepartureAirport:
      formData.departure?.toLowerCase().includes("aéroport") ||
      formData.departure?.toLowerCase().includes("aerop") ||
      formData.departure?.includes("CDG") ||
      formData.departure?.includes("ORY") ||
      formData.departure?.includes("BVA"),
  };

  const arrivalFlightInfo = {
    number: formData.arrivalFlightNumber,
    isArrivalAirport:
      formData.arrival?.toLowerCase().includes("aéroport") ||
      formData.arrival?.toLowerCase().includes("aerop") ||
      formData.arrival?.includes("CDG") ||
      formData.arrival?.includes("ORY") ||
      formData.arrival?.includes("BVA"),
  };

  const departureTrainInfo = {
    number: formData.departureTrainNumber,
    isDepartureStation: formData.departure?.toLowerCase().includes("gare"),
  };

  const arrivalTrainInfo = {
    number: formData.arrivalTrainNumber,
    isArrivalStation: formData.arrival?.toLowerCase().includes("gare"),
  };

  // Estimation de prix (caches + calcul) — `setEstimatedPrice` est ensuite
  // injecté dans l'autocomplétion pour reset le prix à la sélection d'adresse.
  const price = usePriceEstimate(formData, setFormData);

  // Autocomplétion d'adresses (suggestions + filtres POI + debounce/stale-guard)
  const address = useAddressAutocomplete(
    formData,
    setFormData,
    price.setEstimatedPrice,
  );

  // Soumission vers /api/reservation (payload : formData + customerInfo + locale)
  const submit = useReservationSubmit(formData, locale);

  // Helpers étapes (multi-trajets)
  const stops = useStops(formData, setFormData);

  // Récupérer le véhicule sélectionné
  const selectedVehicle = VEHICLES[formData.selectedVehicleId];

  return {
    formData,
    setFormData,
    departureSuggestions: address.departureSuggestions,
    setDepartureSuggestions: address.setDepartureSuggestions,
    arrivalSuggestions: address.arrivalSuggestions,
    setArrivalSuggestions: address.setArrivalSuggestions,
    stopSuggestions: address.stopSuggestions,
    setStopSuggestions: address.setStopSuggestions,
    showDepartureSuggestions: address.showDepartureSuggestions,
    setShowDepartureSuggestions: address.setShowDepartureSuggestions,
    showArrivalSuggestions: address.showArrivalSuggestions,
    setShowArrivalSuggestions: address.setShowArrivalSuggestions,
    showStopSuggestions: address.showStopSuggestions,
    setShowStopSuggestions: address.setShowStopSuggestions,
    isLoadingDeparture: address.isLoadingDeparture,
    setIsLoadingDeparture: address.setIsLoadingDeparture,
    isLoadingArrival: address.isLoadingArrival,
    setIsLoadingArrival: address.setIsLoadingArrival,
    isLoadingStop: address.isLoadingStop,
    setIsLoadingStop: address.setIsLoadingStop,
    estimatedPrice: price.estimatedPrice,
    setEstimatedPrice: price.setEstimatedPrice,
    isCalculatingPrice: price.isCalculatingPrice,
    setIsCalculatingPrice: price.setIsCalculatingPrice,
    priceError: price.priceError,
    setPriceError: price.setPriceError,
    isSubmitting: submit.isSubmitting,
    setIsSubmitting: submit.setIsSubmitting,
    submitError: submit.submitError,
    setSubmitError: submit.setSubmitError,
    coordinatesCache: price.coordinatesCacheRef.current,
    priceCache: price.priceCacheRef.current,
    getCachedCoordinates: price.getCachedCoordinates,
    fetchSuggestions: address.fetchSuggestions,
    handleAddressInput: address.handleAddressInput,
    selectSuggestion: address.selectSuggestion,
    filterByPOIType: address.filterByPOIType,
    addStop: stops.addStop,
    removeStop: stops.removeStop,
    updateStop: stops.updateStop,
    updateStopWaitingTime: stops.updateStopWaitingTime,
    calculatePrice: price.calculatePrice,
    submitReservation: submit.submitReservation,
    resetPriceCache: price.resetPriceCache,
    departureFlightInfo,
    arrivalFlightInfo,
    departureTrainInfo,
    arrivalTrainInfo,
    selectedVehicle,
  };
};
